import { InjectionToken, type Provider, type Signal, inject, signal } from '@angular/core';
import { ActivationEnd, type ActivatedRouteSnapshot, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Project mode: the console's own pages opened under `/projects/:projectId/…`
 * and scoped to that project, against the backend's `/projects/{id}/<app>/…`
 * mounts. Everywhere else the same pages act in the workspace's default
 * project (`/<app>/…`), whatever the project picker remembers.
 */

/** Route parameter that carries the project id in project mode. */
export const PROJECT_ID_PARAM = 'projectId';

/** The Projects landing page; project pages sit beneath it. */
export const PROJECTS_PATH = '/projects';

/** Navigation route templates name the project with this segment. */
export const PROJECT_ROUTE_TEMPLATE = `${PROJECTS_PATH}/:${PROJECT_ID_PARAM}`;

const enc = encodeURIComponent;

/** `/projects/{id}` at the start of a console URL (path, query or fragment may follow). */
const PROJECT_URL = /^\/projects\/([^/?#]+)(?=[/?#]|$)/;

/** `/projects/{id}/tasker|scheduler` anywhere in an API URL (it is absolute by then). */
const PROJECT_API_URL = /\/projects\/([^/?#]+)\/(?:tasker|scheduler)(?=[/?#]|$)/;

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** The project a console URL is scoped to, or `''` outside project mode. */
export function projectIdFromUrl(url: string): string {
  const match = PROJECT_URL.exec(url);
  const id = match?.[1] ?? '';

  return id && !id.startsWith(':') ? decode(id) : '';
}

/** The project an API request is scoped to, or `''` for a default-project call. */
export function projectIdFromApiUrl(url: string): string {
  const match = PROJECT_API_URL.exec(url);

  return match?.[1] ? decode(match[1]) : '';
}

/**
 * The workspace-level path behind a project URL or route template:
 * `/projects/p1/tasker/tasks` and `/projects/:projectId/tasker/tasks` both
 * give `/tasker/tasks`. Anything else comes back unchanged.
 */
export function workspacePathOf(url: string): string {
  const match = PROJECT_URL.exec(url);

  if (!match) {
    return url;
  }

  const rest = url.slice(match[0].length);

  return rest.startsWith('/') ? rest : `/${rest}`;
}

/** `/tasker/tasks` in project `p1` is `/projects/p1/tasker/tasks`; no project leaves it alone. */
export function projectScopedPath(path: string, projectId: string): string {
  const id = projectId.trim();

  return id ? `${PROJECTS_PATH}/${enc(id)}${path}` : path;
}

/** Puts `projectId` into a route template's `:projectId` segment. */
export function fillProjectRoute(route: string, projectId: string): string {
  return route.replace(`:${PROJECT_ID_PARAM}`, enc(projectId));
}

/**
 * Root of an app's backend mount: `/<app>` acts in the workspace's default
 * project, `/projects/{id}/<app>` in that project. The id is a path segment —
 * never a query string or a body field.
 */
export function projectApiBase(app: string, projectId = ''): string {
  const id = projectId.trim();

  return id ? `${PROJECTS_PATH}/${enc(id)}/${app}` : `/${app}`;
}

/**
 * The project the current screen's API calls are scoped to; `''` is the
 * workspace's default project.
 *
 * The root value never changes, so `/tasker/…` and `/scheduler/…` pages
 * always call the default-project endpoints. The `projects/:projectId` route
 * overrides it with {@link provideActiveProjectFromRoute}, and everything
 * resolved beneath that route — page components, their stores and the
 * feature's transport — sees the id from the URL.
 */
export const ACTIVE_PROJECT_ID = new InjectionToken<Signal<string>>('ACTIVE_PROJECT_ID', {
  providedIn: 'root',
  factory: () => signal('').asReadonly(),
});

function projectParamOf(root: ActivatedRouteSnapshot): string {
  let found = '';

  for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
    const value: unknown = route.params[PROJECT_ID_PARAM];

    if (typeof value === 'string' && value) {
      found = value;
    }
  }

  return found;
}

/**
 * Provides {@link ACTIVE_PROJECT_ID} on the `projects/:projectId` route.
 *
 * A route's providers are built once per route configuration and kept for the
 * router's lifetime, so the value cannot be a snapshot of the param: it
 * follows the router. It is read when a page under the route is created (the
 * router has committed the target state by then) and updated as each
 * navigation activates, so a page that is kept from `/projects/a/…` to
 * `/projects/b/…` sees `b`. Leaving project mode keeps the last id, so a page
 * on its way out never calls the default project by mistake.
 */
export function provideActiveProjectFromRoute(): Provider {
  return {
    provide: ACTIVE_PROJECT_ID,
    useFactory: (): Signal<string> => {
      const router = inject(Router);
      const id = signal(projectParamOf(router.routerState.snapshot.root));

      router.events
        .pipe(filter((event): event is ActivationEnd => event instanceof ActivationEnd))
        .subscribe(() => {
          const next = projectParamOf(router.routerState.snapshot.root);

          if (next) {
            id.set(next);
          }
        });

      return id.asReadonly();
    },
  };
}
